import { inject, Injectable } from '@angular/core';
import { Option } from '@sapphire/result';

import { ToastService } from '@ps/core-overlay';
import {
  ComputeFeasibilityParams,
  FeasibilityEstimate,
  FeasibilityAudience,
  FeasibilityCriterion,
  FeasibilityQuotaSet,
  SurveyQualification,
} from '@ps/survey-utils';
import { map, Observable, tap } from 'rxjs';

import { FeasibilityApi } from '../../api/feasibility/feasibility.api';
import { FeasibilityPayloadMapper } from '../../api/feasibility/mappers/feasibility.payload.mapper';

import { FeasibilityAnalysis } from './domain/feasibility-analysis';
import { formatFeasibilityCondition } from './domain/feasibility-condition.formatter';
import { FeasibilityCriteriaMapper } from './domain/feasibility-criteria.mapper';
import { FeasibilityQuotaMapper } from './domain/feasibility-quota.mapper';

@Injectable({ providedIn: 'root' })
export class FeasibilityService {
  private readonly _api = inject(FeasibilityApi);
  private readonly _toast = inject(ToastService);
  private readonly _payloadMapper = new FeasibilityPayloadMapper();
  private readonly _notifiedNamesBySurvey = new Map<number, Set<string>>();

  /**
   * Computes the feasibility of a survey.
   *
   * The feasibility is based on the qualifications and quotas of the survey.
   * When the quota is not used, it is not included in the computation.
   *
   * Notifies when it is determined that a qualification is unused.
   *
   * @param data - survey ID, user, metadata, qualifications and quotas.
   * @returns feasibility - Data built from the feasibility analysis.
   */
  public compute(
    data: ComputeFeasibilityParams,
  ): Observable<FeasibilityEstimate> {
    const { surveyId, user, qualifications } = data;

    // TODO: Remove this when the qualifications are stored correctly in the state.
    const appliedQualifications = this._buildQualifications(qualifications);

    const criteria = new FeasibilityCriteriaMapper(appliedQualifications).map();

    const { payload, quotaSet } = this._payloadMapper.build({
      ...data,
      qualifications: appliedQualifications,
    });

    return this._api.getFeasibility(payload).pipe(
      map((result) => FeasibilityAnalysis.from(result)),
      tap((analysis) => {
        this._notifyUnusedQualifications(
          surveyId,
          analysis,
          quotaSet,
          payload.survey_localization,
          user.email,
        );
      }),
      map((analysis) => {
        return {
          total: analysis.total,
          audience: this._buildFeasibilityAudience(
            criteria,
            analysis.currencySymbol,
          ),
          quotaGroups: new FeasibilityQuotaMapper(analysis).map(quotaSet),
          cpi: analysis.feasibilityCpi,
          suppliers: analysis.suppliers,
        };
      }),
    );
  }

  /**
   * Warns once per computation when a qualification was excluded, then pings
   * internal support unless the buyer entered an invalid range (from > to)
   */
  private _notifyUnusedQualifications(
    surveyId: number,
    analysis: FeasibilityAnalysis,
    quotaSet: FeasibilityQuotaSet,
    localization: string,
    userEmail: string,
  ): void {
    const rows = [...quotaSet.values()].flat();
    const unusedNames = analysis.unusedQualificationNames(rows);
    const notifiedNames = this._notifiedNamesFor(surveyId);
    const newNames = unusedNames.filter((name) => !notifiedNames.has(name));

    if (!newNames.length) {
      return;
    }

    newNames.forEach((name) => notifiedNames.add(name));

    this._toast.warning(
      `You have selected a criteria that needs a feasibility update and was not included in the calculation. We've notified the responsible team and your account representative that ${newNames.join(', ')} needs to be updated — they'll be in touch once it's ready.`,
    );

    if (this._hasInvalidRange(rows)) {
      return;
    }

    const unusedCodes = [
      ...new Set(
        rows
          .filter(
            (row) =>
              analysis.isUnknown(row.qualificationCode, row) &&
              newNames.includes(row.qualificationName?.toUpperCase() ?? 'NA'),
          )
          .map((row) => row.qualificationCode),
      ),
    ];

    this._api
      .notifyUnusedQualification({
        projectId: surveyId,
        user: userEmail,
        criteria: newNames.join(', '),
        qualifications: unusedCodes.join(', '),
        localization,
      })
      .subscribe();
  }

  private _notifiedNamesFor(surveyId: number): Set<string> {
    const existing = this._notifiedNamesBySurvey.get(surveyId);
    if (existing) {
      return existing;
    }

    const created = new Set<string>();
    this._notifiedNamesBySurvey.set(surveyId, created);
    return created;
  }

  private _hasInvalidRange(rows: readonly FeasibilityCriterion[]): boolean {
    return rows.some((row) =>
      Option.from(row.rangeSets?.[0]).mapOr(
        false,
        (range) => range.from > range.to,
      ),
    );
  }

  private _buildFeasibilityAudience(
    criteria: readonly FeasibilityCriterion[],
    currency: string,
  ): FeasibilityAudience[] {
    return criteria.map((criterion) => ({
      label: Option.from(criterion.qualificationName).unwrapOr(''),
      value: formatFeasibilityCondition(criterion, currency),
    }));
  }

  /**
   * Builds qualifications with conditionNames from conditionCodes.
   */
  private _buildQualifications(
    qualifications: SurveyQualification[],
  ): SurveyQualification[] {
    return qualifications.map((qualification) => {
      if (qualification.isRangeType) {
        return qualification;
      }

      return {
        ...qualification,
        data: qualification.data.filter(({ id }) =>
          qualification.conditionCodes.includes(id),
        ),
      };
    });
  }
}
